import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { TrashIcon } from 'lucide-react'
import React, { useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { FileList, FileUpload, IconButton } from 'slab'
import { getGetMemberOnboardingEnrollmentEventsEnrollmentEventIdProofOfQleQueryKey as proofOfQleQueryKey, postMemberOnboardingEnrollmentEventsEnrollmentEventIdProofOfQle, useDeleteMemberOnboardingEnrollmentEventsEnrollmentEventIdProofOfQleFileId } from 'utils/web_api'
import { type MemberOnboardingProofOfQle } from 'utils/web_api.schemas'

import { useOnboarding } from '../OnboardingContext'

// Maximum total documents per enrollment event
// IMPORTANT: Keep in sync with proof_of_qle.rb MAX_TOTAL_DOCUMENTS (when implemented)
const MAX_TOTAL_DOCUMENTS = 20

// Sort files by created_at descending (newest first) to match server ordering
const sortByCreatedAtDesc = (files: MemberOnboardingProofOfQle[]): MemberOnboardingProofOfQle[] => {
  return [...files].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
    return dateB - dateA // Descending order (newest first)
  })
}

const ProofOfQLEFileUpload = ({
  proofOfQle,
  enrollmentEventId,
}: {
  proofOfQle: MemberOnboardingProofOfQle[] | undefined;
  enrollmentEventId: string;
}) => {
  const [error, setError] = useState<string | null>(null)
  const { refreshOnboardingInfo } = useOnboarding()
  const queryClient = useQueryClient()

  /* ===============================
   * Upload File: Update cache upon a successful upload, but still trigger a background refetch to ensure we have the server truth
   * =============================== */

  const uploadProofOfQle = useMutation<MemberOnboardingProofOfQle[], GrapeAPIErrorType, { files: File[] }>({
    mutationFn: async (data: { files: File[] }) => {
      const formData = new FormData()
      data.files.forEach((file) => {
        formData.append('files[]', file)
      })

      return postMemberOnboardingEnrollmentEventsEnrollmentEventIdProofOfQle(
        enrollmentEventId,
        {
          body: formData,
        }
      )
    },
    onSuccess: (newFiles: MemberOnboardingProofOfQle[]) => {
      // Update cache immediately with the new files
      // Sort by created_at descending to match server ordering (desc)
      queryClient.setQueryData(
        proofOfQleQueryKey(enrollmentEventId),
        (oldData: MemberOnboardingProofOfQle[] | undefined) => {
          const merged = [...(oldData || []), ...newFiles]
          return sortByCreatedAtDesc(merged)
        }
      )

      window.segment?.track('[EE] QLE document uploaded', {
        enrollment_event_id: enrollmentEventId,
        file_count: newFiles.length,
      })

      // Background refetch ensures we have the server truth
      refreshData()
    },
    onError: (error: GrapeAPIErrorType) => {
      // Check for field-level validation errors (422 with field_errors)
      // All known attachmentvalidation errors are already triggered from the client before api submission.
      // We are adding this here for any unexpected validation errors
      if (error.status === 422 && error.fieldErrors?.attachments) {
        const attachmentErrors = error.fieldErrors.attachments
        const combinedErrors = attachmentErrors.join(', ')
        setError(combinedErrors)
      } else {
        // For non-validation errors (500, network errors, etc.), show generic message
        setError('Something went wrong uploading the file. Please try again.')
      }
    },
  })

  const onFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList?.length) return false

    const filesToUpload = Array.from(fileList)

    const currentTotal = proofOfQle?.length ?? 0
    if (currentTotal + filesToUpload.length > MAX_TOTAL_DOCUMENTS) {
      setError(
          `You can only upload up to ${MAX_TOTAL_DOCUMENTS} documents total.`
      )
      return
    }

    if (filesToUpload.some((file) => file.size > 20 * 1024 * 1024)) {
      setError('One or more files are too large. Files must be under 20 MB.')
      return
    }

    setError(null)

    uploadProofOfQle.mutate({
      files: filesToUpload,
    })
  }

  /* ===============================
   * Delete File (Optimistic update since a deletion is unlikely to have a validation error)
   * =============================== */

  const deleteProofOfQle = useDeleteMemberOnboardingEnrollmentEventsEnrollmentEventIdProofOfQleFileId({
    mutation: {
      onMutate: async ({ fileId }) => {
        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries({
          queryKey: proofOfQleQueryKey(enrollmentEventId),
        })

        // Snapshot the previous value for rollback
        const previousFiles = queryClient.getQueryData<MemberOnboardingProofOfQle[]>(
          proofOfQleQueryKey(enrollmentEventId)
        )

        // Optimistically remove the file from cache
        queryClient.setQueryData(
          proofOfQleQueryKey(enrollmentEventId),
          (oldData: MemberOnboardingProofOfQle[] | undefined) => {
            return (oldData || []).filter((file) => file.file_id !== fileId)
          }
        )

        // Return context with the previous value for rollback
        return { previousFiles }
      },
      onSuccess: (_data, variables) => {
        setError(null)

        window.segment?.track('[EE] QLE document deleted', {
          enrollment_event_id: enrollmentEventId,
          file_id: variables.fileId,
        })

        // Background refetch ensures we have the server truth
        refreshData()
      },
      onError: (_error, _variables, context) => {
        // Rollback to the previous value on error
        if (context?.previousFiles) {
          queryClient.setQueryData(
            proofOfQleQueryKey(enrollmentEventId),
            context.previousFiles
          )
        }
        setError('Something went wrong deleting the file. Please try again.')
        // Background refetch ensures we have the server truth after error
        refreshData()
      },
    },
  })

  const onRemoveFile = (fileId: string) => {
    deleteProofOfQle.mutate({
      enrollmentEventId,
      fileId,
    })
  }

  /* ===============================
   * Refresh Data from server (background refetch)
   * =============================== */

  const refreshData = () => {
    queryClient.invalidateQueries({
      queryKey: proofOfQleQueryKey(enrollmentEventId),
    })
    refreshOnboardingInfo()
  }

  /* ===============================
   * Render
   * =============================== */

  return (
    <div>
      {uploadProofOfQle.status !== 'pending' && (
        <FileUpload
          multiple
          error={error}
          accept='.pdf,.jpg,.jpeg,.png'
          onChange={onFileUpload}
          description={
            <>
              If your insurance company doesn't approve the documents you uploaded, we'll email you from{' '}
              <a href='mailto:broker@thatch.com' className='underline'>
                broker@thatch.com
              </a>{' '}
              to see if you have something else that might work.
            </>
          }
        >
          <FileUpload.Content layout='horizontal' />
        </FileUpload>
      )}

      {uploadProofOfQle.status === 'pending' && uploadProofOfQle.variables?.files && (
        <div className='mt-10'>
          <FileList className='space-y-4'>
            {uploadProofOfQle.variables.files.map((file, index) => (
              <FileList.File
                key={`uploading-${index}`}
                name={file.name}
                size={file.size}
                status='uploading'
              />
            ))}
          </FileList>
        </div>
      )}

      {(proofOfQle?.length ?? 0) > 0 && (
        <div className='mt-10'>
          <FileList className='space-y-4'>
            <AnimatePresence>
              {(proofOfQle || []).map((file) => (
                <motion.div
                  key={file.file_id}
                  layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <FileList.File
                    name={file.file_name}
                    size={file.file_size}
                    uploadedAt={file.created_at ? new Date(file.created_at) : undefined}
                    status='success'
                    actions={
                      <IconButton
                        icon={TrashIcon}
                        onClick={() => onRemoveFile(file.file_id)}
                        label='Remove'
                        disabled={deleteProofOfQle.isPending}
                      />
                    }
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </FileList>
        </div>
      )}
    </div>
  )
}

export default ProofOfQLEFileUpload
